import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, Button, VStack, HStack, Input, FormControl, FormLabel,
  Box, Text, Badge, useToast, Flex, Select, Checkbox, Icon,
  Divider, InputGroup, InputRightElement, Avatar,
} from '@chakra-ui/react';
import { FiPlus, FiEdit2, FiArrowLeft, FiTrash2, FiEye, FiEyeOff, FiKey, FiShield, FiCamera } from 'react-icons/fi';
import api from '../api';
import ConfirmModal from './ConfirmModal';

const ROLE_LABELS = { admin: 'Admin', user: 'User', restricted: 'Restricted', accounts_manager: 'Accounts Manager' };
const ROLE_COLORS = { admin: 'red', user: 'blue', restricted: 'orange', accounts_manager: 'green' };

// Power Dashboard catalog — keys must match tenants.features and the feature gates in
// pages/Dashboard.js. If a new dashboard is added there, add the key here so it can be
// allow-listed for restricted users.
const POWER_DASHBOARDS = [
  { key: 'pipeline',         label: 'Business Pipeline' },
  { key: 'prospecting',      label: 'Prospecting' },
  { key: 'engagement',       label: 'Engagement' },
  { key: 'companies',        label: 'Companies' },
  { key: 'contacts',         label: 'Contacts' },
  { key: 'book_diary',       label: 'Book My Diary' },
  { key: 'todo',             label: 'To-Do List' },
  { key: 'timesheets',       label: 'Timesheets' },
  { key: 'mileage',          label: 'Mileage' },
  { key: 'site_visitors',    label: 'Site Visitors' },
  { key: 'resources',        label: 'Resources' },
  { key: 'departments',      label: 'Departments' },
  { key: 'products',         label: 'Products' },
  { key: 'supplier_products',label: 'Supplier Products' },
  { key: 'bookkeeper',       label: 'Bookkeeper' },
  { key: 'bank_ledger',      label: 'Bank Ledger' },
  { key: 'nominal_codes',    label: 'Nominal Codes' },
  { key: 'annual_leave',     label: 'Annual Leave' },
  { key: 'payroll',          label: 'Payroll' },
];

function UserBankAccounts({ userId }) {
  const [accounts, setAccounts] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [showForm, setShowForm] = React.useState(false);
  const [editId, setEditId] = React.useState(null);
  const [form, setForm] = React.useState({ bank_name: '', sort_code: '', account_number: '', account_holder: '', iban: '', swift_bic: '' });
  const toast = useToast();

  const fetchAccounts = React.useCallback(async () => {
    try {
      const { data } = await api.get(`/bank-accounts?user_id=${userId}`);
      setAccounts(data.filter(a => !a.deleted_at));
    } catch(e) {}
    setLoading(false);
  }, [userId]);

  React.useEffect(() => { fetchAccounts(); }, [fetchAccounts]);

  const save = async () => {
    if (!form.bank_name) { toast({ title: 'Bank name required', status: 'warning', duration: 2000 }); return; }
    try {
      if (editId) {
        await api.put(`/bank-accounts/${editId}`, form);
      } else {
        await api.post('/bank-accounts', { ...form, user_id: userId, is_own: false });
      }
      toast({ title: editId ? 'Updated' : 'Bank account added', status: 'success', duration: 1500 });
      setShowForm(false); setEditId(null);
      setForm({ bank_name: '', sort_code: '', account_number: '', account_holder: '', iban: '', swift_bic: '' });
      fetchAccounts();
    } catch(e) { toast({ title: 'Failed', status: 'error', duration: 2000 }); }
  };

  const softDelete = async (id) => {
    await api.delete(`/bank-accounts/${id}`);
    fetchAccounts();
  };

  const startEdit = (a) => {
    setForm({ bank_name: a.bank_name, sort_code: a.sort_code || '', account_number: a.account_number || '', account_holder: a.account_holder || '', iban: a.iban || '', swift_bic: a.swift_bic || '' });
    setEditId(a.id); setShowForm(true);
  };

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={2}>
        <Text fontSize="10px" color="gray.500" fontWeight="600" textTransform="uppercase">Bank Accounts (Payroll)</Text>
        <Button size="xs" colorScheme="blue" variant="ghost" leftIcon={<FiPlus size={10} />}
          onClick={() => { setShowForm(p => !p); setEditId(null); setForm({ bank_name: '', sort_code: '', account_number: '', account_holder: '', iban: '', swift_bic: '' }); }}>
          Add
        </Button>
      </Flex>

      {loading ? <Spinner size="xs" /> : accounts.length === 0 && !showForm
        ? <Text fontSize="10px" color="gray.400" mb={2}>No bank accounts — add one to use with payroll</Text>
        : null}

      {accounts.map(a => (
        <Box key={a.id} p={2} bg="gray.50" borderRadius="md" mb={1} border="1px solid" borderColor="gray.200">
          <Flex justify="space-between" align="flex-start">
            <Box>
              <HStack spacing={2} mb={0.5}>
                <Text fontSize="11px" fontWeight="700">{a.bank_name}</Text>
                {a.is_default && <Badge colorScheme="green" fontSize="7px">Default</Badge>}
              </HStack>
              {a.account_holder && <Text fontSize="10px" color="gray.600">{a.account_holder}</Text>}
              <Text fontSize="10px" color="gray.500">
                {[a.sort_code, a.account_number].filter(Boolean).join(' / ')}
              </Text>
              {a.iban && <Text fontSize="9px" color="gray.400">IBAN: {a.iban}</Text>}
              {a.swift_bic && <Text fontSize="9px" color="gray.400">SWIFT: {a.swift_bic}</Text>}
            </Box>
            <HStack spacing={1}>
              <IconButton icon={<FiEdit2 size={10} />} size="xs" variant="ghost" onClick={() => startEdit(a)} aria-label="Edit" />
              <IconButton icon={<FiTrash2 size={10} />} size="xs" variant="ghost" colorScheme="red" onClick={() => softDelete(a.id)} aria-label="Delete" />
            </HStack>
          </Flex>
        </Box>
      ))}

      {showForm && (
        <Box p={3} bg="blue.50" borderRadius="md" border="1px solid" borderColor="blue.200" mt={1}>
          <Text fontSize="10px" fontWeight="700" color="blue.700" mb={2}>{editId ? 'Edit Bank Account' : 'Add Bank Account'}</Text>
          <VStack spacing={2}>
            <HStack w="100%">
              <FormControl isRequired flex={2}>
                <FormLabel fontSize="9px" mb={0.5}>Bank Name</FormLabel>
                <Input size="xs" value={form.bank_name} onChange={e => setForm(p => ({ ...p, bank_name: e.target.value }))} placeholder="e.g. Barclays" />
              </FormControl>
              <FormControl flex={2}>
                <FormLabel fontSize="9px" mb={0.5}>Account Holder</FormLabel>
                <Input size="xs" value={form.account_holder} onChange={e => setForm(p => ({ ...p, account_holder: e.target.value }))} />
              </FormControl>
            </HStack>
            <HStack w="100%">
              <FormControl flex={1}>
                <FormLabel fontSize="9px" mb={0.5}>Sort Code</FormLabel>
                <Input size="xs" value={form.sort_code} onChange={e => setForm(p => ({ ...p, sort_code: e.target.value }))} placeholder="00-00-00" />
              </FormControl>
              <FormControl flex={2}>
                <FormLabel fontSize="9px" mb={0.5}>Account Number</FormLabel>
                <Input size="xs" value={form.account_number} onChange={e => setForm(p => ({ ...p, account_number: e.target.value }))} />
              </FormControl>
            </HStack>
            <HStack w="100%">
              <FormControl flex={2}>
                <FormLabel fontSize="9px" mb={0.5}>IBAN</FormLabel>
                <Input size="xs" value={form.iban} onChange={e => setForm(p => ({ ...p, iban: e.target.value }))} />
              </FormControl>
              <FormControl flex={1}>
                <FormLabel fontSize="9px" mb={0.5}>SWIFT/BIC</FormLabel>
                <Input size="xs" value={form.swift_bic} onChange={e => setForm(p => ({ ...p, swift_bic: e.target.value }))} />
              </FormControl>
            </HStack>
            <HStack w="100%" justify="flex-end" pt={1}>
              <Button size="xs" variant="ghost" onClick={() => { setShowForm(false); setEditId(null); }}>Cancel</Button>
              <Button size="xs" colorScheme="blue" onClick={save}>{editId ? 'Update' : 'Add Account'}</Button>
            </HStack>
          </VStack>
        </Box>
      )}
    </Box>
  );
}

export default function UserManagementModal({ isOpen, onClose }) {
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [view, setView] = useState('list'); // list, detail, edit, create, password, permissions
  const [form, setForm] = useState({});
  const [passwordForm, setPasswordForm] = useState({ password: '', confirm: '' });
  const [showPw, setShowPw] = useState(false);
  const [saving, setSaving] = useState(false);
  const avatarInputRef = React.useRef(null);

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selected) return;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder', 'avatars');
    try {
      const { data } = await api.post('/files/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      await api.put(`/users/${selected.id}`, { avatar_file_id: data.id });
      setSelected(prev => ({ ...prev, avatar_file_id: data.id, avatar_url: `${data.folder}/${data.stored_name}` }));
      toast({ title: 'Avatar uploaded', status: 'success', duration: 1500 });
      await fetchUsers();
    } catch(err) { toast({ title: 'Upload failed', status: 'error', duration: 2000 }); }
  };
  const toast = useToast();
  const [confirmDel, setConfirmDel] = useState(null);

  // Data for permissions
  const [allCompanies, setAllCompanies] = useState([]);
  const [allRowGroups, setAllRowGroups] = useState([]);
  const [allProjects, setAllProjects] = useState([]);
  const [permCompanyIds, setPermCompanyIds] = useState([]);
  const [permRowGroupIds, setPermRowGroupIds] = useState([]);
  const [permProjectIds, setPermProjectIds] = useState([]);
  const [permDashboardKeys, setPermDashboardKeys] = useState([]);
  const [projectSearch, setProjectSearch] = useState('');

  const fetchUsers = useCallback(async () => {
    try {
      const { data } = await api.get('/users');
      setUsers(data);
    } catch (e) {}
  }, []);

  useEffect(() => {
    if (isOpen) { fetchUsers(); setView('list'); setSelected(null); }
  }, [isOpen, fetchUsers]);

  const openDetail = async (user) => {
    try {
      const { data } = await api.get(`/users/${user.id}`);
      setSelected(data);
      setView('detail');
    } catch (e) {}
  };

  const openCreate = () => {
    setForm({ username: '', password: '', display_name: '', email: '', role: 'user' });
    setView('create');
  };

  const openEdit = () => {
    setForm({ display_name: selected.display_name || '', email: selected.email || '', role: selected.role });
    setView('edit');
  };

  const openPassword = () => {
    setPasswordForm({ password: '', confirm: '' });
    setShowPw(false);
    setView('password');
  };

  const openPermissions = async () => {
    try {
      const [coRes, rgRes, prRes] = await Promise.all([
        api.get('/companies'),
        api.get('/row-groups'),
        api.get('/projects'),
      ]);
      setAllCompanies(coRes.data);
      setAllRowGroups(rgRes.data);
      setAllProjects(Array.isArray(prRes.data) ? prRes.data.filter(p => !p.is_template) : []);
      setPermCompanyIds(selected.allowed_companies?.map(c => c.company_id) || []);
      setPermRowGroupIds(selected.allowed_row_groups?.map(r => r.row_group_id) || []);
      setPermProjectIds(selected.allowed_projects?.map(p => p.project_id) || []);
      setPermDashboardKeys(selected.allowed_dashboards || []);
      setProjectSearch('');
      setView('permissions');
    } catch (e) {}
  };

  const handleCreate = async () => {
    if (!form.email || !form.password) {
      toast({ title: 'Email and password required', status: 'warning', duration: 2000 });
      return;
    }
    setSaving(true);
    try {
      await api.post('/users', form);
      toast({ title: 'User created', status: 'success', duration: 1500 });
      await fetchUsers();
      setView('list');
    } catch (e) {
      toast({ title: e.response?.data?.error || 'Failed', status: 'error', duration: 2000 });
    } finally { setSaving(false); }
  };

  const handleUpdate = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/users/${selected.id}`, form);
      toast({ title: 'User updated', status: 'success', duration: 1500 });
      await fetchUsers();
      await openDetail({ id: selected.id });
    } catch (e) {
      toast({ title: 'Failed to update', status: 'error', duration: 2000 });
    } finally { setSaving(false); }
  };

  const handleSetPassword = async () => {
    if (passwordForm.password !== passwordForm.confirm) {
      toast({ title: 'Passwords do not match', status: 'warning', duration: 2000 });
      return;
    }
    if (passwordForm.password.length < 6) {
      toast({ title: 'Password must be at least 6 characters', status: 'warning', duration: 2000 });
      return;
    }
    setSaving(true);
    try {
      await api.put(`/users/${selected.id}/password`, { password: passwordForm.password });
      toast({ title: 'Password updated', status: 'success', duration: 1500 });
      setView('detail');
    } catch (e) {
      toast({ title: 'Failed to update password', status: 'error', duration: 2000 });
    } finally { setSaving(false); }
  };

  const handleSavePermissions = async () => {
    setSaving(true);
    try {
      await api.put(`/users/${selected.id}/permissions`, {
        allowed_company_ids: permCompanyIds,
        allowed_row_group_ids: permRowGroupIds,
        allowed_project_ids: permProjectIds,
        allowed_dashboard_keys: permDashboardKeys,
      });
      toast({ title: 'Permissions saved', status: 'success', duration: 1500 });
      await openDetail({ id: selected.id });
    } catch (e) {
      toast({ title: 'Failed to save permissions', status: 'error', duration: 2000 });
    } finally { setSaving(false); }
  };

  const handleDelete = (id) => setConfirmDel(id);
  const doDelete = async () => { try { await api.delete(`/users/${confirmDel}`); await fetchUsers(); toast({ title:'User deleted', status:'info', duration:1500 }); } catch(e){} finally { setConfirmDel(null); } };

  const goBack = () => {
    if (view === 'edit' || view === 'password' || view === 'permissions') setView('detail');
    else { setView('list'); setSelected(null); }
  };

  const toggleCompany = (id) => {
    setPermCompanyIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleRowGroup = (id) => {
    setPermRowGroupIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleProject = (id) => {
    setPermProjectIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleDashboard = (key) => {
    setPermDashboardKeys(prev => prev.includes(key) ? prev.filter(x => x !== key) : [...prev, key]);
  };

  return (
    <>
    <Modal isOpen={isOpen} onClose={onClose} size="lg" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent maxH="85vh">
        <ModalHeader fontSize="md">
          <Flex align="center" gap={2}>
            {view !== 'list' && <Icon as={FiArrowLeft} cursor="pointer" onClick={goBack} />}
            User Management
            {view === 'list' && (
              <Button size="xs" leftIcon={<FiPlus />} colorScheme="blue" ml="auto" onClick={openCreate}>
                Add User
              </Button>
            )}
          </Flex>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>

          {/* ── LIST ── */}
          {view === 'list' && (
            <VStack spacing={1} align="stretch">
              {users.map(u => (
                <Box key={u.id} p={2} border="1px" borderColor="gray.100" borderRadius="md"
                  cursor="pointer" _hover={{ bg: 'gray.50' }} onClick={() => openDetail(u)}>
                  <Flex justify="space-between" align="center">
                    <HStack spacing={2}>
                      <Avatar size="xs" name={u.display_name || u.username}
                        src={u.avatar_url ? `/api/files/${u.avatar_url}` : undefined} />
                      <Box>
                        <Flex align="center" gap={2}>
                          <Text fontSize="12px" fontWeight="600">{u.display_name || u.username}</Text>
                          <Badge colorScheme={ROLE_COLORS[u.role]} fontSize="9px">{ROLE_LABELS[u.role]}</Badge>
                        </Flex>
                        <Text fontSize="10px" color="gray.500">@{u.username}{u.email ? ` • ${u.email}` : ''}</Text>
                      </Box>
                    </HStack>
                  </Flex>
                </Box>
              ))}
              {users.length === 0 && <Text fontSize="sm" color="gray.500" textAlign="center" py={4}>No users</Text>}
            </VStack>
          )}

          {/* ── DETAIL ── */}
          {view === 'detail' && selected && (
            <VStack spacing={4} align="stretch">
              <Flex justify="space-between" align="center">
                <HStack spacing={3}>
                  <Box position="relative" cursor="pointer" onClick={() => avatarInputRef.current?.click()}>
                    <Avatar size="md" name={selected.display_name || selected.username}
                      src={selected.avatar_url ? `/api/files/${selected.avatar_url}` : undefined} />
                    <Box position="absolute" bottom="-2px" right="-2px" bg="blue.500" borderRadius="full" p={0.5}>
                      <FiCamera size={10} color="white" />
                    </Box>
                    <input ref={avatarInputRef} type="file" accept="image/*" hidden onChange={handleAvatarUpload} />
                  </Box>
                  <Box>
                    <Text fontSize="lg" fontWeight="600">{selected.display_name || selected.username}</Text>
                    <Text fontSize="12px" color="gray.500">@{selected.username}</Text>
                  </Box>
                </HStack>
                <Badge colorScheme={ROLE_COLORS[selected.role]} fontSize="10px" px={2} py={1}>
                  {ROLE_LABELS[selected.role]}
                </Badge>
              </Flex>

              {selected.email && (
                <Box>
                  <Text fontSize="10px" color="gray.500" fontWeight="600" textTransform="uppercase">Email</Text>
                  <Text fontSize="12px">{selected.email}</Text>
                </Box>
              )}

              <Divider />

              <HStack spacing={2} flexWrap="wrap">
                <Button size="sm" leftIcon={<FiEdit2 />} onClick={openEdit}>Edit Details</Button>
                <Button size="sm" leftIcon={<FiKey />} onClick={openPassword}>Set Password</Button>
                {selected.role === 'restricted' && (
                  <Button size="sm" leftIcon={<FiShield />} colorScheme="orange" onClick={openPermissions}>
                    Permissions
                  </Button>
                )}
                <Button size="sm" leftIcon={<FiTrash2 />} colorScheme="red" variant="ghost"
                  onClick={() => handleDelete(selected.id)}>
                  Delete
                </Button>
              </HStack>

              {selected.role === 'restricted' && (
                <>
                  <Divider />
                  <Box>
                    <Text fontSize="11px" fontWeight="600" color="gray.600" mb={1}>Allowed Companies</Text>
                    {selected.allowed_companies?.length > 0 ? (
                      <Flex gap={1} flexWrap="wrap">
                        {selected.allowed_companies.map(c => (
                          <Badge key={c.company_id} colorScheme="teal" fontSize="9px">{c.name}</Badge>
                        ))}
                      </Flex>
                    ) : <Text fontSize="11px" color="gray.400">None (no company access)</Text>}
                  </Box>
                  <Box>
                    <Text fontSize="11px" fontWeight="600" color="gray.600" mb={1}>Allowed Row Groups</Text>
                    {selected.allowed_row_groups?.length > 0 ? (
                      <Flex gap={1} flexWrap="wrap">
                        {selected.allowed_row_groups.map(r => (
                          <Badge key={r.row_group_id} colorScheme="blue" fontSize="9px">{r.name}</Badge>
                        ))}
                      </Flex>
                    ) : <Text fontSize="11px" color="gray.400">None (no row group access)</Text>}
                  </Box>
                  <Box>
                    <Text fontSize="11px" fontWeight="600" color="gray.600" mb={1}>Allowed Projects (direct grants)</Text>
                    {selected.allowed_projects?.length > 0 ? (
                      <Flex gap={1} flexWrap="wrap">
                        {selected.allowed_projects.map(p => (
                          <Badge key={p.project_id} colorScheme="purple" fontSize="9px">{p.project_name}</Badge>
                        ))}
                      </Flex>
                    ) : <Text fontSize="11px" color="gray.400">None — only group/company access plus auto-grant from assignments</Text>}
                  </Box>
                  <Box>
                    <Text fontSize="11px" fontWeight="600" color="gray.600" mb={1}>Allowed Power Dashboards</Text>
                    {selected.allowed_dashboards?.length > 0 ? (
                      <Flex gap={1} flexWrap="wrap">
                        {selected.allowed_dashboards.map(k => {
                          const d = POWER_DASHBOARDS.find(x => x.key === k);
                          return <Badge key={k} colorScheme="orange" fontSize="9px">{d ? d.label : k}</Badge>;
                        })}
                      </Flex>
                    ) : <Text fontSize="11px" color="gray.400">All tenant-enabled dashboards (no per-user restriction set)</Text>}
                  </Box>
                </>
              )}

              <Divider />
              <UserBankAccounts userId={selected.id} />
            </VStack>
          )}

          {/* ── CREATE ── */}
          {view === 'create' && (
            <VStack spacing={3}>
              <FormControl>
                <FormLabel fontSize="xs" mb={0.5}>Email address *</FormLabel>
                <Input size="sm" type="email" value={form.email} onChange={(e) => setForm(p => ({ ...p, email: e.target.value }))} autoFocus />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="xs" mb={0.5}>Password *</FormLabel>
                <InputGroup size="sm">
                  <Input type={showPw ? 'text' : 'password'} value={form.password}
                    onChange={(e) => setForm(p => ({ ...p, password: e.target.value }))} />
                  <InputRightElement>
                    <Icon as={showPw ? FiEyeOff : FiEye} cursor="pointer" color="gray.400" boxSize={3}
                      onClick={() => setShowPw(!showPw)} />
                  </InputRightElement>
                </InputGroup>
              </FormControl>
              <FormControl>
                <FormLabel fontSize="xs" mb={0.5}>Display Name</FormLabel>
                <Input size="sm" value={form.display_name} onChange={(e) => setForm(p => ({ ...p, display_name: e.target.value }))} />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="xs" mb={0.5}>Role</FormLabel>
                <Select size="sm" value={form.role} onChange={(e) => setForm(p => ({ ...p, role: e.target.value }))}>
                  <option value="admin">Admin</option>
                  <option value="user">User</option>
                  <option value="restricted">Restricted</option>
                  <option value="accounts_manager">Accounts Manager</option>
                </Select>
              </FormControl>
              <HStack w="100%" justify="flex-end" pt={2}>
                <Button size="sm" variant="ghost" onClick={goBack}>Cancel</Button>
                <Button size="sm" bg="brand.600" color="white" _hover={{ bg: 'brand.700' }}
                  onClick={handleCreate} isLoading={saving}>Create User</Button>
              </HStack>
            </VStack>
          )}

          {/* ── EDIT ── */}
          {view === 'edit' && (
            <VStack spacing={3}>
              <FormControl>
                <FormLabel fontSize="xs" mb={0.5}>Display Name</FormLabel>
                <Input size="sm" value={form.display_name} onChange={(e) => setForm(p => ({ ...p, display_name: e.target.value }))} />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="xs" mb={0.5}>Email</FormLabel>
                <Input size="sm" type="email" value={form.email} onChange={(e) => setForm(p => ({ ...p, email: e.target.value }))} />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="xs" mb={0.5}>Role</FormLabel>
                <Select size="sm" value={form.role} onChange={(e) => setForm(p => ({ ...p, role: e.target.value }))}>
                  <option value="admin">Admin</option>
                  <option value="user">User</option>
                  <option value="restricted">Restricted</option>
                  <option value="accounts_manager">Accounts Manager</option>
                </Select>
              </FormControl>
              <HStack w="100%" justify="flex-end" pt={2}>
                <Button size="sm" variant="ghost" onClick={goBack}>Cancel</Button>
                <Button size="sm" bg="brand.600" color="white" _hover={{ bg: 'brand.700' }}
                  onClick={handleUpdate} isLoading={saving}>Save</Button>
              </HStack>
            </VStack>
          )}

          {/* ── PASSWORD ── */}
          {view === 'password' && (
            <VStack spacing={3}>
              <Text fontSize="sm" color="gray.600">Set a new password for <b>{selected.display_name}</b></Text>
              <FormControl>
                <FormLabel fontSize="xs" mb={0.5}>New Password</FormLabel>
                <InputGroup size="sm">
                  <Input type={showPw ? 'text' : 'password'} value={passwordForm.password}
                    onChange={(e) => setPasswordForm(p => ({ ...p, password: e.target.value }))} />
                  <InputRightElement>
                    <Icon as={showPw ? FiEyeOff : FiEye} cursor="pointer" color="gray.400" boxSize={3}
                      onClick={() => setShowPw(!showPw)} />
                  </InputRightElement>
                </InputGroup>
              </FormControl>
              <FormControl>
                <FormLabel fontSize="xs" mb={0.5}>Confirm Password</FormLabel>
                <Input size="sm" type={showPw ? 'text' : 'password'} value={passwordForm.confirm}
                  onChange={(e) => setPasswordForm(p => ({ ...p, confirm: e.target.value }))} />
              </FormControl>
              {passwordForm.password && passwordForm.confirm && passwordForm.password !== passwordForm.confirm && (
                <Text fontSize="11px" color="red.500">Passwords do not match</Text>
              )}
              <HStack w="100%" justify="flex-end" pt={2}>
                <Button size="sm" variant="ghost" onClick={goBack}>Cancel</Button>
                <Button size="sm" bg="brand.600" color="white" _hover={{ bg: 'brand.700' }}
                  onClick={handleSetPassword} isLoading={saving}
                  isDisabled={!passwordForm.password || passwordForm.password !== passwordForm.confirm}>
                  Set Password
                </Button>
              </HStack>
            </VStack>
          )}

          {/* ── PERMISSIONS (restricted users only) ── */}
          {view === 'permissions' && (
            <VStack spacing={4} align="stretch">
              <Text fontSize="sm" color="gray.600">
                Configure what <b>{selected.display_name}</b> can see. They will only see projects matching the rules below — granted directly, on an allowed company, in an allowed row group, or where they're an assigned project/task user. Power dashboards are limited to the allow list. Leave dashboards empty to grant access to all tenant-enabled dashboards.
              </Text>

              <Box>
                <Text fontSize="12px" fontWeight="600" mb={2}>Allowed Companies <Text as="span" fontWeight="400" color="gray.500">— grants all projects on these companies</Text></Text>
                <VStack spacing={1} align="stretch" maxH="160px" overflowY="auto">
                  {allCompanies.map(co => (
                    <Box key={co.id} px={2} py={1} borderRadius="sm" cursor="pointer"
                      bg={permCompanyIds.includes(co.id) ? 'teal.50' : 'white'}
                      _hover={{ bg: permCompanyIds.includes(co.id) ? 'teal.100' : 'gray.50' }}
                      onClick={() => toggleCompany(co.id)}>
                      <Checkbox size="sm" isChecked={permCompanyIds.includes(co.id)}
                        onChange={() => toggleCompany(co.id)} colorScheme="teal">
                        <Text fontSize="12px">{co.name}</Text>
                      </Checkbox>
                    </Box>
                  ))}
                  {allCompanies.length === 0 && <Text fontSize="11px" color="gray.400">No companies</Text>}
                </VStack>
              </Box>

              <Box>
                <Text fontSize="12px" fontWeight="600" mb={2}>Allowed Row Groups <Text as="span" fontWeight="400" color="gray.500">— grants every project in these groups, including future additions</Text></Text>
                <VStack spacing={1} align="stretch" maxH="160px" overflowY="auto">
                  {allRowGroups.map(rg => (
                    <Box key={rg.id} px={2} py={1} borderRadius="sm" cursor="pointer"
                      bg={permRowGroupIds.includes(rg.id) ? 'blue.50' : 'white'}
                      _hover={{ bg: permRowGroupIds.includes(rg.id) ? 'blue.100' : 'gray.50' }}
                      onClick={() => toggleRowGroup(rg.id)}>
                      <Checkbox size="sm" isChecked={permRowGroupIds.includes(rg.id)}
                        onChange={() => toggleRowGroup(rg.id)} colorScheme="blue">
                        <Text fontSize="12px">{rg.name}</Text>
                      </Checkbox>
                    </Box>
                  ))}
                </VStack>
              </Box>

              <Box>
                <HStack justify="space-between" mb={2}>
                  <Text fontSize="12px" fontWeight="600">Allowed Projects <Text as="span" fontWeight="400" color="gray.500">— grants individual projects (in addition to company/group grants)</Text></Text>
                  <Text fontSize="11px" color="gray.500">{permProjectIds.length} selected</Text>
                </HStack>
                <Input size="xs" placeholder="Search projects…"
                  value={projectSearch}
                  onChange={(e) => setProjectSearch(e.target.value)}
                  mb={2}
                />
                <VStack spacing={1} align="stretch" maxH="200px" overflowY="auto">
                  {(() => {
                    const q = projectSearch.trim().toLowerCase();
                    const selectedFirst = [...allProjects].sort((a, b) => {
                      const sa = permProjectIds.includes(a.id) ? 0 : 1;
                      const sb = permProjectIds.includes(b.id) ? 0 : 1;
                      if (sa !== sb) return sa - sb;
                      return (a.project_name || '').localeCompare(b.project_name || '');
                    });
                    const filtered = q
                      ? selectedFirst.filter(p => (p.project_name || '').toLowerCase().includes(q) || (p.company_name || '').toLowerCase().includes(q))
                      : selectedFirst;
                    if (filtered.length === 0) return <Text fontSize="11px" color="gray.400">No matching projects</Text>;
                    return filtered.slice(0, 200).map(p => (
                      <Box key={p.id} px={2} py={1} borderRadius="sm" cursor="pointer"
                        bg={permProjectIds.includes(p.id) ? 'purple.50' : 'white'}
                        _hover={{ bg: permProjectIds.includes(p.id) ? 'purple.100' : 'gray.50' }}
                        onClick={() => toggleProject(p.id)}>
                        <Checkbox size="sm" isChecked={permProjectIds.includes(p.id)}
                          onChange={() => toggleProject(p.id)} colorScheme="purple">
                          <Text fontSize="12px" noOfLines={1}>
                            {p.project_name}
                            {p.company_name && <Text as="span" color="gray.500" fontSize="10px"> — {p.company_name}</Text>}
                          </Text>
                        </Checkbox>
                      </Box>
                    ));
                  })()}
                </VStack>
              </Box>

              <Box>
                <HStack justify="space-between" mb={2}>
                  <Text fontSize="12px" fontWeight="600">Allowed Power Dashboards</Text>
                  <Text fontSize="11px" color="gray.500">
                    {permDashboardKeys.length === 0 ? 'all enabled (default)' : `${permDashboardKeys.length} selected`}
                  </Text>
                </HStack>
                <Text fontSize="11px" color="gray.500" mb={2}>
                  Leave all unchecked to give access to every tenant-enabled dashboard.
                  Tick specific ones to restrict the user to only those.
                </Text>
                <VStack spacing={1} align="stretch" maxH="200px" overflowY="auto">
                  {POWER_DASHBOARDS.map(d => (
                    <Box key={d.key} px={2} py={1} borderRadius="sm" cursor="pointer"
                      bg={permDashboardKeys.includes(d.key) ? 'orange.50' : 'white'}
                      _hover={{ bg: permDashboardKeys.includes(d.key) ? 'orange.100' : 'gray.50' }}
                      onClick={() => toggleDashboard(d.key)}>
                      <Checkbox size="sm" isChecked={permDashboardKeys.includes(d.key)}
                        onChange={() => toggleDashboard(d.key)} colorScheme="orange">
                        <Text fontSize="12px">{d.label}</Text>
                      </Checkbox>
                    </Box>
                  ))}
                </VStack>
              </Box>

              <HStack w="100%" justify="flex-end" pt={2}>
                <Button size="sm" variant="ghost" onClick={goBack}>Cancel</Button>
                <Button size="sm" bg="brand.600" color="white" _hover={{ bg: 'brand.700' }}
                  onClick={handleSavePermissions} isLoading={saving}>
                  Save Permissions
                </Button>
              </HStack>
            </VStack>
          )}

        </ModalBody>
        <ModalFooter><Button size="sm" onClick={onClose}>Close</Button></ModalFooter>
      </ModalContent>
    </Modal>
    <ConfirmModal isOpen={!!confirmDel} onClose={() => setConfirmDel(null)}
      title="Delete User" message="Are you sure you want to delete this user? They will no longer be able to log in."
      confirmLabel="Delete" confirmColor="red" onConfirm={doDelete} />
    </>
  );
}
